import React, { useState, useEffect } from 'react';
import Sidebar from '../components/AdminSidebar';
import '../styles/AdminMail.css';

export default function AdminMail() {
    // Navigation state
    const [activeTab, setActiveTab] = useState('new-message');
    
    // Statistics state
    const [stats, setStats] = useState({
        totalRecipients: 85,
        lecturersCount: 15,
        lastSent: '12.11.2025, 14:32'
    });

    // Recipients state
    const [quickSelection, setQuickSelection] = useState('everyone');
    const [selectedUserTypes, setSelectedUserTypes] = useState({
        administrators: true,
        lecturers: false,
        students: true
    });
    const [selectedYear, setSelectedYear] = useState('2025/2026');
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [estimatedRecipients, setEstimatedRecipients] = useState(85);
    const [showUserSelector, setShowUserSelector] = useState(false);
    const [allUsers, setAllUsers] = useState([]);
    const [userSearchTerm, setUserSearchTerm] = useState('');

    // Message state
    const [messageType, setMessageType] = useState({
        email: true,
        notification: false
    });
    const [messageSubject, setMessageSubject] = useState('');
    const [messageContent, setMessageContent] = useState('');
    const [sendOptions, setSendOptions] = useState({
        copyToMe: false,
        scheduleLater: false
    });

    // Data state
    const [groups, setGroups] = useState([]);
    const [academicYears, setAcademicYears] = useState(['2024/2025', '2025/2026', '2026/2027']);

    // Fetch groups and other data
    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch groups with academic year information
                const groupsResponse = await fetch(`${process.env.REACT_APP_API_URL}/courses/getGroups`);
                if (groupsResponse.ok) {
                    const groupsData = await groupsResponse.json();
                    setGroups(groupsData);
                }

                // Fetch statistics
                const statsResponse = await fetch(`${process.env.REACT_APP_API_URL}/mail/statistics`);
                if (statsResponse.ok) {
                    const statsData = await statsResponse.json();
                    setStats({
                        totalRecipients: statsData.totalRecipients,
                        lecturersCount: statsData.lecturersCount,
                        lastSent: statsData.lastSent
                    });
                }

                // Fetch academic years
                const yearsResponse = await fetch(`${process.env.REACT_APP_API_URL}/mail/academicYears`);
                if (yearsResponse.ok) {
                    const yearsData = await yearsResponse.json();
                    if (yearsData.length > 0) {
                        setAcademicYears(yearsData);
                        // Set default selected year to the first (most recent) year if not already set
                        setSelectedYear(prev => {
                            if (!prev || !yearsData.includes(prev)) {
                                return yearsData[0];
                            }
                            return prev;
                        });
                    }
                }

                // Fetch all users for selection
                const usersResponse = await fetch(`${process.env.REACT_APP_API_URL}/mail/users`, {
                    credentials: 'include'
                });
                if (usersResponse.ok) {
                    const usersData = await usersResponse.json();
                    setAllUsers(usersData);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        fetchData();
    }, []);

    // Handle quick selection
    const handleQuickSelection = (selection) => {
        setQuickSelection(selection);
        if (selection === 'everyone') {
            setSelectedUserTypes({
                administrators: true,
                lecturers: true,
                students: true
            });
        } else if (selection === 'administrators') {
            setSelectedUserTypes({
                administrators: true,
                lecturers: false,
                students: false
            });
        } else if (selection === 'lecturers') {
            setSelectedUserTypes({
                administrators: false,
                lecturers: true,
                students: false
            });
        } else if (selection === 'students') {
            setSelectedUserTypes({
                administrators: false,
                lecturers: false,
                students: true
            });
        }
    };

    // Handle group selection
    const handleGroupToggle = (groupCode) => {
        setSelectedGroups(prev => {
            if (prev.includes(groupCode)) {
                return prev.filter(code => code !== groupCode);
            } else {
                return [...prev, groupCode];
            }
        });
    };

    // Calculate estimated recipients
    useEffect(() => {
        const fetchRecipientCount = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/mail/recipients/count`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        userTypes: selectedUserTypes,
                        academicYear: selectedYear,
                        groups: selectedGroups,
                        selectedUsers: selectedUsers.length > 0 ? selectedUsers : undefined
                    })
                });

                if (response.ok) {
                    const data = await response.json();
                    setEstimatedRecipients(data.count);
                }
            } catch (error) {
                console.error('Error fetching recipient count:', error);
            }
        };

        fetchRecipientCount();
    }, [selectedUserTypes, selectedGroups, selectedYear, selectedUsers]);

    // Handle send message
    const handleSendMessage = async () => {
        if (!messageSubject.trim() || !messageContent.trim()) {
            alert('Proszę wypełnić temat i treść wiadomości.');
            return;
        }

        try {
            const payload = {
                subject: messageSubject,
                content: messageContent,
                messageType: messageType.email ? 'email' : 'notification',
                userTypes: selectedUserTypes,
                academicYear: selectedYear,
                groups: selectedGroups,
                selectedUsers: selectedUsers.length > 0 ? selectedUsers : undefined,
                sendOptions: sendOptions
            };

            const response = await fetch(`${process.env.REACT_APP_API_URL}/mail/send`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const data = await response.json();
                alert(`Wiadomość została wysłana pomyślnie do ${data.recipientsCount} odbiorców!`);
                setMessageSubject('');
                setMessageContent('');
                setSelectedGroups([]);
                setSelectedUsers([]);
                
                // Refresh statistics
                const statsResponse = await fetch(`${process.env.REACT_APP_API_URL}/mail/statistics`);
                if (statsResponse.ok) {
                    const statsData = await statsResponse.json();
                    setStats({
                        totalRecipients: statsData.totalRecipients,
                        lecturersCount: statsData.lecturersCount,
                        lastSent: statsData.lastSent
                    });
                }
            } else {
                const errorData = await response.json();
                alert(errorData.error || 'Błąd podczas wysyłania wiadomości.');
            }
        } catch (error) {
            console.error('Error sending message:', error);
            alert('Błąd podczas wysyłania wiadomości.');
        }
    };

    // Handle save draft
    const handleSaveDraft = async () => {
        if (!messageSubject.trim() && !messageContent.trim()) {
            alert('Proszę wypełnić temat lub treść wiadomości przed zapisaniem szkicu.');
            return;
        }

        try {
            const draft = {
                subject: messageSubject || '(Brak tematu)',
                content: messageContent || '(Brak treści)',
                userTypes: selectedUserTypes,
                academicYear: selectedYear,
                groups: selectedGroups
            };

            const response = await fetch(`${process.env.REACT_APP_API_URL}/mail/draft`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(draft)
            });

            if (response.ok) {
                alert('Szkic został zapisany pomyślnie!');
            } else {
                const errorData = await response.json();
                alert(errorData.error || 'Błąd podczas zapisywania szkicu.');
            }
        } catch (error) {
            console.error('Error saving draft:', error);
            alert('Błąd podczas zapisywania szkicu.');
        }
    };

    return (
        <div className='admin-mail'>
            <Sidebar />
            <div className='main-content'>
                <div className='header'>
                    <h1 className='header-title'>Wiadomości</h1>
                </div>
                <div className='dashboard'>
                    <h2 className='page-title'>Wiadomości e-mail</h2>
                    <p className='page-subtitle'>Wysyłaj wewnętrzne wiadomości e-mail do administratorów, wykładowców i kursantów za pomocą serwera pocztowego.</p>

                    {/* Navigation Tabs */}
                    <div className='mail-nav-tabs'>
                        <button 
                            className={`nav-tab ${activeTab === 'new-message' ? 'active' : ''}`}
                            onClick={() => setActiveTab('new-message')}
                        >
                            Nowa wiadomość
                        </button>
                        <button 
                            className={`nav-tab ${activeTab === 'history' ? 'active' : ''}`}
                            onClick={() => setActiveTab('history')}
                        >
                            Historia wysyłek
                        </button>
                        <button 
                            className={`nav-tab ${activeTab === 'templates' ? 'active' : ''}`}
                            onClick={() => setActiveTab('templates')}
                        >
                            Szablony wiadomości
                        </button>
                        <button 
                            className={`nav-tab ${activeTab === 'settings' ? 'active' : ''}`}
                            onClick={() => setActiveTab('settings')}
                        >
                            Ustawienia serwera
                        </button>
                    </div>

                    {/* Statistics Section */}
                    <div className='mail-stats-section'>
                        <div className='stat-card-mail'>
                            <div className='stat-label'>Adresaci w organizacji</div>
                            <div className='stat-value'>{stats.totalRecipients}</div>
                        </div>
                        <div className='stat-card-mail'>
                            <div className='stat-label'>Liczba wykładowców</div>
                            <div className='stat-value'>{stats.lecturersCount}</div>
                        </div>
                        <div className='stat-card-mail'>
                            <div className='stat-label'>Ostatnia wysyłka</div>
                            <div className='stat-value'>{stats.lastSent}</div>
                        </div>
                    </div>

                    {/* Main Content - Two Columns */}
                    {activeTab === 'new-message' && (
                        <div className='mail-content-wrapper'>
                            {/* Left Column - Recipients */}
                            <div className='mail-card recipients-card'>
                                <h3 className='card-title'>Odbiorcy</h3>
                                
                                {/* Quick Selections */}
                                <div className='section-block'>
                                    <div className='section-label'>SZYBKIE WYBORY</div>
                                    <div className='quick-selection-buttons'>
                                        <button 
                                            className={`quick-btn ${quickSelection === 'everyone' ? 'active' : ''}`}
                                            onClick={() => handleQuickSelection('everyone')}
                                        >
                                            Wszyscy w organizacji
                                        </button>
                                        <button 
                                            className={`quick-btn ${quickSelection === 'administrators' ? 'active' : ''}`}
                                            onClick={() => handleQuickSelection('administrators')}
                                        >
                                            Tylko administratorzy
                                        </button>
                                        <button 
                                            className={`quick-btn ${quickSelection === 'lecturers' ? 'active' : ''}`}
                                            onClick={() => handleQuickSelection('lecturers')}
                                        >
                                            Tylko wykładowcy
                                        </button>
                                        <button 
                                            className={`quick-btn ${quickSelection === 'students' ? 'active' : ''}`}
                                            onClick={() => handleQuickSelection('students')}
                                        >
                                            Tylko kursanci
                                        </button>
                                    </div>
                                </div>

                                {/* User Types */}
                                <div className='section-block'>
                                    <div className='section-label'>TYP UŻYTKOWNIKÓW</div>
                                    <div className='user-types'>
                                        <label className='checkbox-label'>
                                            <input 
                                                type='checkbox'
                                                checked={selectedUserTypes.administrators}
                                                onChange={(e) => setSelectedUserTypes(prev => ({ ...prev, administrators: e.target.checked }))}
                                            />
                                            Administratorzy
                                        </label>
                                        <label className='checkbox-label'>
                                            <input 
                                                type='checkbox'
                                                checked={selectedUserTypes.lecturers}
                                                onChange={(e) => setSelectedUserTypes(prev => ({ ...prev, lecturers: e.target.checked }))}
                                            />
                                            Wykładowcy
                                        </label>
                                        <label className='checkbox-label'>
                                            <input 
                                                type='checkbox'
                                                checked={selectedUserTypes.students}
                                                onChange={(e) => setSelectedUserTypes(prev => ({ ...prev, students: e.target.checked }))}
                                            />
                                            Kursanci
                                        </label>
                                    </div>
                                </div>

                                {/* Narrow Recipients */}
                                <div className='section-block'>
                                    <div className='section-label'>ZAWĘŻ ODBIORCÓW</div>
                                    
                                    {/* Select Individual Users */}
                                    <div className='filter-group'>
                                        <div className='filter-label'>Wybierz użytkowników</div>
                                        <button
                                            type='button'
                                            className='btn btn-secondary'
                                            onClick={() => setShowUserSelector(true)}
                                            style={{ marginBottom: '10px' }}
                                        >
                                            {selectedUsers.length > 0 
                                                ? `Wybrano ${selectedUsers.length} użytkowników` 
                                                : 'Wybierz użytkowników'}
                                        </button>
                                        {selectedUsers.length > 0 && (
                                            <div style={{ marginTop: '10px', fontSize: '0.9em', color: '#666' }}>
                                                Wybrano {selectedUsers.length} {selectedUsers.length === 1 ? 'użytkownika' : 'użytkowników'}
                                                <button
                                                    type='button'
                                                    onClick={() => setSelectedUsers([])}
                                                    style={{ marginLeft: '10px', color: '#d32f2f', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer' }}
                                                >
                                                    Wyczyść
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Academic Year */}
                                    <div className='filter-group'>
                                        <div className='filter-label'>Rocznik</div>
                                        <div className='year-buttons'>
                                            {academicYears.map(year => (
                                                <button
                                                    key={year}
                                                    className={`year-btn ${selectedYear === year ? 'active' : ''}`}
                                                    onClick={() => setSelectedYear(year)}
                                                >
                                                    {year}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Groups */}
                                    <div className='filter-group'>
                                        <div className='filter-label'>Grupy</div>
                                        <div className='group-buttons'>
                                            {groups
                                                .filter(group => group.academic_year === selectedYear)
                                                .map(group => (
                                                    <button
                                                        key={group.group_code}
                                                        className={`group-btn ${selectedGroups.includes(group.group_code) ? 'active' : ''}`}
                                                        onClick={() => handleGroupToggle(group.group_code)}
                                                    >
                                                        {group.group_name} ({selectedYear})
                                                    </button>
                                                ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Estimated Recipients */}
                                <div className='estimated-recipients'>
                                    <strong>Szacowana liczba odbiorców:</strong> {estimatedRecipients} użytkowników
                                </div>
                            </div>

                            {/* Right Column - New Message */}
                            <div className='mail-card message-card'>
                                <h3 className='card-title'>Nowa wiadomość</h3>
                                
                                {/* Message Type */}
                                <div className='section-block'>
                                    <label className='checkbox-label'>
                                        <input 
                                            type='checkbox'
                                            checked={messageType.email}
                                            onChange={(e) => setMessageType({ email: e.target.checked, notification: !e.target.checked })}
                                        />
                                        Wyślij e-mail
                                    </label>
                                    <label className='checkbox-label'>
                                        <input 
                                            type='radio'
                                            name='messageType'
                                            checked={messageType.notification}
                                            onChange={(e) => setMessageType({ email: !e.target.checked, notification: e.target.checked })}
                                        />
                                        Wyślij powiadomienie w systemie
                                    </label>
                                </div>

                                {/* Message Subject */}
                                <div className='form-group'>
                                    <label className='form-label'>Temat wiadomości</label>
                                    <input 
                                        type='text'
                                        className='form-input'
                                        placeholder='Np. Zmiana terminu zajęć dla rocznika 2025/2026'
                                        value={messageSubject}
                                        onChange={(e) => setMessageSubject(e.target.value)}
                                    />
                                </div>

                                {/* Message Content */}
                                <div className='form-group'>
                                    <label className='form-label'>Treść</label>
                                    <textarea 
                                        className='form-textarea'
                                        placeholder='Dzień dobry, informujemy, że...'
                                        value={messageContent}
                                        onChange={(e) => setMessageContent(e.target.value)}
                                        rows={8}
                                    />
                                </div>

                                {/* Sending Options */}
                                <div className='section-block'>
                                    <label className='radio-label'>
                                        <input 
                                            type='radio'
                                            name='sendOptions'
                                            checked={sendOptions.copyToMe}
                                            onChange={(e) => setSendOptions({ copyToMe: e.target.checked, scheduleLater: false })}
                                        />
                                        Wyślij kopię do mnie
                                    </label>
                                    <label className='radio-label'>
                                        <input 
                                            type='radio'
                                            name='sendOptions'
                                            checked={sendOptions.scheduleLater}
                                            onChange={(e) => setSendOptions({ copyToMe: false, scheduleLater: e.target.checked })}
                                        />
                                        Zaplanuj wysyłkę na później
                                    </label>
                                </div>

                                {/* Summary */}
                                <div className='recipients-summary'>
                                    <strong>Odbiorcy:</strong> {selectedGroups.length} {selectedGroups.length === 1 ? 'grupa' : selectedGroups.length < 5 ? 'grupy' : 'grup'}, {academicYears.filter(y => y === selectedYear).length} {academicYears.filter(y => y === selectedYear).length === 1 ? 'rocznik' : 'roczniki'}, {estimatedRecipients} {estimatedRecipients === 1 ? 'użytkownik' : 'użytkowników'}
                                </div>

                                {/* Action Buttons */}
                                <div className='message-actions'>
                                    <button className='btn btn-secondary' onClick={handleSaveDraft}>
                                        Zapisz jako szkic
                                    </button>
                                    <button className='btn btn-primary' onClick={handleSendMessage}>
                                        Wyślij wiadomość
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Other Tabs Content (Placeholder) */}
                    {activeTab === 'history' && (
                        <div className='mail-card'>
                            <h3 className='card-title'>Historia wysyłek</h3>
                            <p>Funkcjonalność będzie dostępna po podłączeniu backendu.</p>
                        </div>
                    )}

                    {activeTab === 'templates' && (
                        <div className='mail-card'>
                            <h3 className='card-title'>Szablony wiadomości</h3>
                            <p>Funkcjonalność będzie dostępna po podłączeniu backendu.</p>
                        </div>
                    )}

                    {activeTab === 'settings' && (
                        <div className='mail-card'>
                            <h3 className='card-title'>Ustawienia serwera</h3>
                            <p>Funkcjonalność będzie dostępna po podłączeniu backendu.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* User Selection Modal */}
            {showUserSelector && (
                <div className='user-selector-modal-overlay' onClick={() => setShowUserSelector(false)}>
                    <div className='user-selector-modal' onClick={(e) => e.stopPropagation()}>
                        <div className='user-selector-header'>
                            <h3>Wybierz użytkowników</h3>
                            <button 
                                className='user-selector-close'
                                onClick={() => setShowUserSelector(false)}
                            >
                                ×
                            </button>
                        </div>
                        <div className='user-selector-search'>
                            <input
                                type='text'
                                placeholder='Szukaj użytkownika...'
                                value={userSearchTerm}
                                onChange={(e) => setUserSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className='user-selector-list'>
                            {allUsers
                                .filter(user => {
                                    const searchLower = userSearchTerm.toLowerCase();
                                    return !userSearchTerm || 
                                        user.full_name?.toLowerCase().includes(searchLower) ||
                                        user.username?.toLowerCase().includes(searchLower) ||
                                        user.email?.toLowerCase().includes(searchLower);
                                })
                                .map(user => (
                                    <label key={user.user_id} className='user-selector-item'>
                                        <input
                                            type='checkbox'
                                            checked={selectedUsers.includes(user.user_id)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedUsers([...selectedUsers, user.user_id]);
                                                } else {
                                                    setSelectedUsers(selectedUsers.filter(id => id !== user.user_id));
                                                }
                                            }}
                                        />
                                        <div className='user-selector-item-info'>
                                            <div className='user-selector-item-name'>{user.full_name || user.username}</div>
                                            <div className='user-selector-item-details'>
                                                {user.email && <span>{user.email}</span>}
                                                <span className={`user-type-badge user-type-${user.user_type}`}>
                                                    {user.user_type === 'student' ? 'Kursant' : 
                                                     user.user_type === 'lecturer' ? 'Wykładowca' : 
                                                     user.user_type === 'administrator' ? 'Administrator' : user.user_type}
                                                </span>
                                                {user.group_name && <span>Grupa: {user.group_name}</span>}
                                            </div>
                                        </div>
                                    </label>
                                ))}
                        </div>
                        <div className='user-selector-footer'>
                            <button 
                                className='btn btn-secondary'
                                onClick={() => setShowUserSelector(false)}
                            >
                                Zamknij
                            </button>
                            <button 
                                className='btn btn-primary'
                                onClick={() => setShowUserSelector(false)}
                            >
                                Zastosuj ({selectedUsers.length})
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
